<template>
  <div class="p-tabs">
    <!-- PrimeVue Tabs shell -->
    <Tabs :value="activeId">
      <TabList>
        <Tab
          v-for="t in tabs"
          :key="t.id"
          :value="t.id"
          as="div"
          class="flex items-center p-tab px-4 py-2"
          :data-testid="`contenttab-${props.dataKey}-tab-${t.id}`"
          :data-tab-id="t.id"
          @click="select(t.id)"
        >
          <SlotRenderer
            v-if="t.header"
            :slotFn="t.header"
            :props="{ isActive: t.id === activeId, index: t.index }"
          />
          <template v-else>
            {{ t.label ?? 'Tab ' + (t.index + 1) }}
          </template>
        </Tab>
      </TabList>

      <div class="p-tabpanels">
        <div
          v-for="t in tabs"
          :key="t.id + '-panel'"
          :class="['p-tabpanel', t.id === activeId ? 'tab-open' : 'tab-closed']"
          :data-testid="`contenttab-${props.dataKey}-panel-${t.id}`"
          :data-tab-id="t.id"
        >
          <SlotRenderer
            :slotFn="t.content"
            :props="{ isActive: t.id === activeId, index: t.index }"
          />
        </div>
      </div>
    </Tabs>

    <!-- Keep children mounted so they can register (do NOT use v-if here) -->
    <div v-show="false" aria-hidden="true">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * ContentTabs
 * - Uses PrimeVue Tabs/TabList/Tab for consistent look & feel.
 * - Persists active tab under the provided `dataKey` using your existing store.
 * - Children register themselves via <ContentTab>.
 * - No routing here — purely manual `dataKey`.
 */

import { defineComponent, nextTick, onMounted, type PropType, provide, ref, watch } from 'vue';
import Tab from 'primevue/tab';
import TabList from 'primevue/tablist';
import Tabs from 'primevue/tabs';

import { useSelectedTabsStore } from '@/stores/selectedSubcomponentTabs';

type SlotFn = ((props?: Record<string, any>) => any) | undefined;

type TabEntry = {
  id: string; // stable id for persistence (from <ContentTab name> or auto index)
  index: number; // order index
  header?: SlotFn; // header slot function
  content: SlotFn; // content slot function (required)
  label?: string; // optional fallback label
};

const props = defineProps<{
  dataKey: string; // key under which the active tab is stored in your existing store
}>();

const selectedTabsStore = useSelectedTabsStore();

// registry filled by <ContentTab> children
const tabs = ref<TabEntry[]>([]);
const activeId = ref<string>('');

// internal counter to preserve insertion order
let counter = 0;

// Register/unregister API exposed to <ContentTab>
const registerTab = (payload: {
  id?: string;
  header?: SlotFn;
  content?: SlotFn;
  label?: string;
}) => {
  const id = payload.id ?? String(counter);
  const entry: TabEntry = {
    id,
    index: counter++,
    header: payload.header,
    content: payload.content,
    label: payload.label,
  };
  tabs.value.push(entry);
};

const unregisterTab = (id: string) => {
  tabs.value = tabs.value.filter((t) => t.id !== id);
};

provide('persisted-tabs:register', registerTab);
provide('persisted-tabs:unregister', unregisterTab);

const initActive = () => {
  const stored = selectedTabsStore.getSelectedTab(props.dataKey);
  const exists = tabs.value.some((t) => t.id === stored);
  if (stored && exists) {
    activeId.value = stored;
  } else {
    activeId.value = tabs.value[0]?.id ?? '';
  }
};

const select = (id: string) => {
  if (!tabs.value.some((t) => t.id === id)) return;
  activeId.value = id;
  selectedTabsStore.setSelectedTab(props.dataKey, id); // persist to existing store
};

// Re-init when the list of child tabs changes (on mount or dynamic v-if)
watch(
  tabs,
  async (v) => {
    if (!v.length) return;
    await nextTick();
    if (!activeId.value || !v.some((t) => t.id === activeId.value)) {
      initActive();
    }
  },
  { deep: true },
);

onMounted(async () => {
  await nextTick(); // ensure children had a chance to register
  if (tabs.value.length) initActive();
});

// Helper to render slot functions
const SlotRenderer = defineComponent({
  name: 'SlotRenderer',
  props: {
    slotFn: { type: Function as PropType<SlotFn>, default: undefined },
    props: {
      type: Object as PropType<Record<string, any>>,
      required: false,
      default: () => ({}),
    },
  },
  setup(p) {
    return () => (p.slotFn ? p.slotFn(p.props) : null);
  },
});
</script>

<style scoped lang="css">
@layer app {
  .p-tabs {
    background: transparent;
    overflow-x: hidden;
  }
  /* :deep() */
  .p-tablist-tab-list {
    background: transparent;
  }
  .p-tabpanels {
    background: transparent;
  }

  /* Keep panels mounted; just collapse visually */
  .tab-closed {
    position: relative;
    overflow: hidden;
    height: 0;
  }
}
</style>
